import InputGroupContent from '@/components/elements/input'
import { Box,  Flex,  Heading,  } from '@chakra-ui/react'
import Submit from '@/components/elements/submit'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from '@/store/store'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { useState } from 'react'
import Toast from '@/components/elements/toast'
import { userUpdateSchema } from '@/entities/schemas/users'
import type { InferType } from 'yup'
import SpinnerLoading from '@/components/elements/spinner'
import { updateUserFn } from '@/store/features/users'
import {
	sessionRefrestUpdate,
	usersRefrestUpdate,
} from '@/store/features/state'
import UsersEntity, { UsersPositions } from '@/entities/api/users/users'
import { userUpdateById } from '@/services/api/users/users'
import PermissionsZone from './parts/users/permissions'
import { useFormErrorToast } from '@/hooks/useFormErrorToast'

type UserFormValues = InferType<typeof userUpdateSchema>

const EditUser = ({ close }: { close: () => void }) => {
	const user = useSelector((state: RootState) => state.users.selectUser)
	if (!user) return
	const token = useSelector((state: RootState) => state.auth.token)
	const {
		register,
		control,
		handleSubmit,
		formState: { errors },
	} = useForm<UserFormValues>({
		resolver: yupResolver(userUpdateSchema),
	})
	useFormErrorToast(errors)
	const dispatch = useDispatch<AppDispatch>()
	const [isLoading, setIsloading] = useState<boolean>(false)

	const showPermissions =
		(user.position as UsersPositions) === UsersPositions.administration

	const onSubmit = async (dataYup: UserFormValues) => {
		try {
			setIsloading(true)
			const data: Partial<UsersEntity> = {
				name: dataYup.name,
				lastname: dataYup.lastname,
				permissions: dataYup.permissions ? dataYup.permissions : [],
			}

			const response = await userUpdateById(token ?? '', user._id ?? '', data)

			if (response?.status === 409) {
				Toast({
					type: 'error',
					message: 'Hubo un error al actualizar el usuario',
				})
				return
			}

			if (response?.status === 401) {
				dispatch(sessionRefrestUpdate())
				return
			}

			setIsloading(false)
			Toast({ type: 'success', message: 'Usuario actualizado correctamente' })
			dispatch(updateUserFn({ ...user, ...data }))
			dispatch(usersRefrestUpdate())
			close()
		} catch (error) {
			setIsloading(false)
			Toast({ type: 'error', message: 'No se pudo crear el usuario' })
			close()
		}
	}

	return (
		<form onSubmit={handleSubmit(onSubmit)}>
			<Flex w='100%' h='full' flexDirection='column' alignItems='center'>
				<Box
					w='full'
					display='flex'
					justifyContent='start'
					alignItems='center'
					mb='2'
				>
					<Heading
						as='h4'
						fontSize={{ base: 'lg', md: 'xl' }}
						color='black'
						fontWeight='900'
					>
						Informacion general
					</Heading>
				</Box>
				<InputGroupContent
					label='Nombre del usuario'
					placeholder='Ingresa el nombre usuario'
					name='name'
					type='text'
					defaultValue={user.name}
					register={register}
					errors={errors}
				/>
				<InputGroupContent
					label='Apellidos del usuario'
					placeholder='Ingresa los apellidos del usuario'
					name='lastname'
					type='text'
					defaultValue={user.lastname}
					register={register}
					errors={errors}
				/>
				{showPermissions && (
					<Box w='full' mb='6'>
						<Box
							w='full'
							display='flex'
							justifyContent='start'
							alignItems='center'
							mb='4'
						>
							<Heading
								as='h4'
								fontSize={{ base: 'lg', md: 'xl' }}
								color='black'
								fontWeight='900'
							>
								Permisos del usuario
							</Heading>
						</Box>
						<PermissionsZone
							control={control}
							errors={errors}
							showPermissions={showPermissions}
							isEdit={true}
							permissions={user.permissions}
						/>
					</Box>
				)}
				<Box pt='4' pb='8'>
					{isLoading ? (
						<SpinnerLoading color='black' />
					) : (
						<Submit text='Editar usuario' />
					)}
				</Box>
			</Flex>
		</form>
	)
}

export default EditUser
